import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Checkbox,
  DropdownMenu,
  EmptyState,
  Icon,
  IconButton,
  Paginator,
  Popover,
  Spinner,
  Tag,
  TextField,
  Tooltip,
  TreeTable,
  type TreeNode,
  type TreeTableColumn,
} from '@humanforce/mango-ui-react';
import type {
  PayRunExceptionItem,
  PayRunExceptionsPage,
  PayRunPopulationEmployee,
  PayRunPopulationPage,
  PopulationLoadStatus,
} from './payRunPopulationApiTypes';
import { actionPayRunComplianceFinding, getPayRunExceptions, getPopulation } from './payRunPopulationHttpClient';
import { PayRunBankingPanel } from './PayRunBankingPanel';
import type { BsbValidationDetails, BsbValidationFinding } from './bsbValidationApiTypes';
import {
  bankingFindingGuidance,
  bankingFindingLabel,
  bankingFindingStatus,
  workerFindings,
} from './payRunBankingModel';
import './PayRunExceptionsTab.css';

interface PayRunExceptionsTabProps {
  payId: string;
  businessRecordId: string;
  populationStatus: PopulationLoadStatus | null | undefined;
  reloadKey?: number;
  onFindingActioned?: () => Promise<void> | void;
  /** Banking (BSB) evidence owned by the Pay Run detail screen and shared with the timeline. */
  banking: BsbValidationDetails | null;
  bankingLoading: boolean;
  bankingError: string | null;
  bankingNotEvaluated: boolean;
  onReloadBanking: () => Promise<void> | void;
  onNotice?: (notice: { tone: 'positive' | 'critical'; title: string; message: string }) => void;
}

interface ComplianceEmployeeRow extends PayRunPopulationEmployee {
  kind: 'employee';
  issues: PayRunExceptionItem[];
  bankingFindings: BsbValidationFinding[];
  issueTone: 'neutral' | 'critical' | 'caution';
  statusLabel: 'Review required' | 'Overridden';
  synthetic?: boolean;
}

interface ComplianceIssueRow extends PayRunExceptionItem {
  kind: 'issue';
}

interface ComplianceBankingRow extends BsbValidationFinding {
  kind: 'banking';
}

interface ComplianceGroupRow {
  kind: 'group';
  label: 'Critical' | 'Warnings';
  count: number;
  issueTone: 'critical' | 'caution';
}

const pageSize = 20;
type ComplianceFilter = 'critical' | 'warning' | 'overridden';
const complianceFilterOptions = [
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warnings' },
  { value: 'overridden', label: 'Overridden' },
];
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const number = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 2 });
const words = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const isCritical = (severity: string): boolean => ['blocking', 'cannotstartpay', 'critical', 'failed']
  .some((value) => severity.toLowerCase().replace(/[_\s-]/g, '').includes(value));

const belongsToEmployee = (issue: PayRunExceptionItem, employee: PayRunPopulationEmployee): boolean => {
  const references = [issue.personId, issue.workEntityId, issue.contextId].filter(Boolean);
  return references.includes(employee.personId) || references.includes(employee.employmentRecordId);
};

const bankingBelongsToEmployee = (finding: BsbValidationFinding, employee: PayRunPopulationEmployee): boolean =>
  finding.personId === employee.personId || finding.employmentRecordId === employee.employmentRecordId;

const bankingIsCritical = (finding: BsbValidationFinding): boolean =>
  finding.severity === 'blocker' && !finding.overridden;

const issuePresentationAction = (issue: PayRunExceptionItem) => {
  const reference = issue.sourceReference?.toLowerCase();
  if (reference === 'bankaccountid' || reference === 'paymentdestinations') return 'Add bank account';
  if (reference === 'superfundid' || reference === 'superfundusi') return 'Run lookup';
  return null;
};

const TruncatedTableText = ({ text, className = '' }: { text: string; className?: string }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;
    const measure = () => setTruncated(element.scrollWidth > element.clientWidth);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
    };
  }, [text]);

  const content = (
    <span
      ref={textRef}
      className={`pay-run-compliance-truncated-text ${className}`.trim()}
      tabIndex={truncated ? 0 : undefined}
    >
      {text}
    </span>
  );

  return (
    <span className="pay-run-compliance-truncated-shell">
      {truncated ? <Tooltip content={text}>{content}</Tooltip> : content}
    </span>
  );
};

export const PayRunExceptionsTab = ({
  payId, businessRecordId, populationStatus, reloadKey, onFindingActioned,
  banking, bankingLoading, bankingError, bankingNotEvaluated, onReloadBanking, onNotice,
}: PayRunExceptionsTabProps) => {
  const [page, setPage] = useState(1);
  const [exceptions, setExceptions] = useState<PayRunExceptionsPage | null>(null);
  const [population, setPopulation] = useState<PayRunPopulationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<(string | number)[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<Record<string, boolean>>({
    'group-critical': true,
    'group-warnings': true,
  });

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const [nextExceptions, nextPopulation] = await Promise.all([
        getPayRunExceptions(payId, { page, pageSize }, signal),
        getPopulation(payId, { page, pageSize, search: '' }, signal),
      ]);
      setExceptions(nextExceptions);
      setPopulation(nextPopulation);
    } catch (loadError) {
      if (!signal?.aborted) setError(loadError instanceof Error ? loadError.message : 'Compliance details could not be loaded.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [page, payId]);

  const actionFinding = useCallback(async (findingId: string) => {
    setActioningId(findingId);
    setError(null);
    try {
      await actionPayRunComplianceFinding(payId, findingId);
      await load();
      await onFindingActioned?.();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'The finding could not be actioned.');
    } finally {
      setActioningId(null);
    }
  }, [load, onFindingActioned, payId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, populationStatus, reloadKey]);

  const rows = useMemo<ComplianceEmployeeRow[]>(() => {
    if (!exceptions || !population) return [];
    const currentIssues = exceptions.items.filter((issue) => issue.lifecycle !== 'RESOLVED');
    const currentBankingFindings = workerFindings(banking);
    const employeeRows = population.items.flatMap((employee) => {
      const issues = currentIssues.filter((issue) => belongsToEmployee(issue, employee));
      const employeeBankingFindings = currentBankingFindings.filter((finding) => bankingBelongsToEmployee(finding, employee));
      if (issues.length === 0 && employeeBankingFindings.length === 0) return [];
      return [{
        ...employee,
        kind: 'employee' as const,
        issues,
        bankingFindings: employeeBankingFindings,
        issueTone: issues.some((issue) => isCritical(issue.severity)) || employeeBankingFindings.some(bankingIsCritical)
          ? 'critical' as const
          : issues.length > 0 || employeeBankingFindings.some((finding) => !finding.overridden)
            ? 'caution' as const
            : 'neutral' as const,
        statusLabel: issues.length === 0 && employeeBankingFindings.length > 0
          && employeeBankingFindings.every((finding) => finding.overridden)
          ? 'Overridden' as const
          : 'Review required' as const,
      }];
    });
    const matchedIssueIds = new Set(employeeRows.flatMap((employee) => employee.issues.map((issue) => issue.id)));
    const matchedBankingFindingIds = new Set(employeeRows
      .flatMap((employee) => employee.bankingFindings.map((finding) => finding.findingId)));
    const unmatchedGroups = new Map<string, PayRunExceptionItem[]>();
    currentIssues.filter((issue) => !matchedIssueIds.has(issue.id)).forEach((issue) => {
      const owner = issue.personId ?? issue.workEntityId ?? issue.contextId ?? issue.id;
      unmatchedGroups.set(owner, [...(unmatchedGroups.get(owner) ?? []), issue]);
    });
    const unmatchedRows = [...unmatchedGroups.entries()].map(([owner, issues]): ComplianceEmployeeRow => ({
      kind: 'employee',
      personId: owner,
      employmentRecordId: `compliance-${owner}`,
      employeeNumber: owner,
      displayName: issues[0].displayName ?? 'Pay Run issue',
      roleTitle: 'Compliance review',
      employmentStartDate: '',
      employmentEndDate: null,
      payrollStatus: 'review_required',
      exceptionSeverity: issues.some((issue) => isCritical(issue.severity)) ? 'blocking' : 'warning',
      units: Number.NaN,
      hourlyUnits: Number.NaN,
      gross: Number.NaN,
      taxable: Number.NaN,
      tax: Number.NaN,
      stsl: Number.NaN,
      super: Number.NaN,
      netPay: Number.NaN,
      transactions: [],
      issues,
      bankingFindings: [],
      issueTone: issues.some((issue) => isCritical(issue.severity)) ? 'critical' : 'caution',
      statusLabel: 'Review required',
      synthetic: true,
    }));
    const unmatchedBankingGroups = new Map<string, BsbValidationFinding[]>();
    currentBankingFindings.filter((finding) => !matchedBankingFindingIds.has(finding.findingId)).forEach((finding) => {
      const owner = finding.personId || finding.employmentRecordId || finding.findingId;
      unmatchedBankingGroups.set(owner, [...(unmatchedBankingGroups.get(owner) ?? []), finding]);
    });
    // Population is authoritative when it is paginated: findings are attached only to workers on the current server
    // page and the paginator navigates to subsequent employees. When the complete population fits on one page, a
    // synthetic fallback keeps a malformed or historical finding visible instead of dropping it silently.
    const unmatchedBankingRows = population.pagination.totalPages <= 1
      ? [...unmatchedBankingGroups.entries()].map(([owner, findings]): ComplianceEmployeeRow => ({
        kind: 'employee',
        personId: findings[0].personId || owner,
        employmentRecordId: findings[0].employmentRecordId || `banking-${owner}`,
        employeeNumber: findings[0].employmentRecordId || owner,
        displayName: findings[0].workerDisplayName ?? 'Worker banking finding',
        roleTitle: 'Banking review',
        employmentStartDate: '', employmentEndDate: null,
        payrollStatus: 'review_required', exceptionSeverity: findings.some(bankingIsCritical) ? 'blocking' : 'warning',
        units: Number.NaN, hourlyUnits: Number.NaN, gross: Number.NaN, taxable: Number.NaN, tax: Number.NaN,
        stsl: Number.NaN, super: Number.NaN, netPay: Number.NaN, transactions: [], issues: [],
        bankingFindings: findings,
        issueTone: findings.some(bankingIsCritical) ? 'critical'
          : findings.some((finding) => !finding.overridden) ? 'caution' : 'neutral',
        statusLabel: findings.every((finding) => finding.overridden) ? 'Overridden' : 'Review required',
        synthetic: true,
      }))
      : [];
    return [...employeeRows, ...unmatchedRows, ...unmatchedBankingRows];
  }, [banking, exceptions, population]);

  const visibleRows = useMemo(() => {
    const filterValues = new Set(selectedFilters as ComplianceFilter[]);
    const query = search.trim().toLowerCase();
    return rows.filter((employee) => {
      const matchesSearch = !query || [
        employee.displayName,
        employee.employeeNumber,
        employee.roleTitle,
        ...employee.issues.flatMap((issue) => [issue.code, issue.displayName, issue.message]),
        ...employee.bankingFindings.flatMap((finding) => [finding.bsb, finding.ruleCode, finding.workerDisplayName]),
      ].some((value) => String(value ?? '').toLowerCase().includes(query));
      const matchesFilter = filterValues.size === 0
        || (filterValues.has('critical') && employee.issueTone === 'critical')
        || (filterValues.has('warning') && employee.issueTone === 'caution')
        || (filterValues.has('overridden') && employee.statusLabel === 'Overridden');
      return matchesSearch && matchesFilter;
    });
  }, [rows, search, selectedFilters]);

  const columnsFor = useCallback((onOverrideBanking: (finding: BsbValidationFinding) => void, expansionHeader: ReactNode): TreeTableColumn[] => {
    const data = (node: TreeNode) => node.data as unknown as ComplianceGroupRow | ComplianceEmployeeRow | ComplianceIssueRow | ComplianceBankingRow;
    const bodyClassName = (value: unknown) => {
      const row = value as ComplianceGroupRow | ComplianceEmployeeRow | ComplianceIssueRow | ComplianceBankingRow;
      const employeeKey = row.kind === 'employee' ? `employee-${row.personId}-${row.employmentRecordId}` : '';
      const isExpandedEmployee = row.kind === 'employee' && expandedKeys[employeeKey];
      return `pay-run-compliance-table__cell${row.kind === 'group' ? ' pay-run-compliance-table__cell--group' : ''}${isExpandedEmployee ? ' pay-run-compliance-table__cell--expanded-parent' : ''}`;
    };
    const presentation = {
      bodyClassName,
      headerClassName: 'pay-run-compliance-table__header',
    };
    const money = (value: number) => Number.isFinite(value) ? currency.format(value) : '—';
    const quantity = (value: number) => Number.isFinite(value) ? number.format(value) : '—';
    return [
      {
        field: 'displayName', header: expansionHeader as unknown as string, width: '15.125rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'group') {
            return <span className={`pay-run-compliance-group-label mgo-color-${row.issueTone}`}>{row.label} ({row.count})</span>;
          }
          if (row.kind === 'banking') {
            const critical = bankingIsCritical(row);
            const guidance = bankingFindingGuidance(row);
            return (
              <div className="pay-run-compliance-issue-block" data-testid={`banking-finding-${row.findingId}`}>
                <div
                  className={`pay-run-compliance-issue-card mgo-background-${critical ? 'critical' : 'caution'} mgo-border-${critical ? 'critical' : 'caution'}-soft`}
                  role="note"
                >
                  <span className={`pay-run-compliance-issue-icon mgo-text-xs mgo-color-${critical ? 'critical' : 'caution'}`}>
                    <Icon name={critical ? 'status-critical' : 'status-caution'} />
                  </span>
                  <span className="pay-run-compliance-issue-copy mgo-text-s">
                    <strong>{bankingFindingLabel(row.ruleCode)} · {row.bsb || 'BSB unavailable'} · {bankingFindingStatus(row)}</strong>
                    {guidance ? <span>{guidance}</span> : null}
                    {row.supersededByBsb ? <span>Replacement BSB: {row.supersededByBsb}</span> : null}
                  </span>
                  {row.overrideAllowed && !row.overridden ? (
                    <Button label="Override" variant="secondary" onClick={() => onOverrideBanking(row)} />
                  ) : null}
                </div>
              </div>
            );
          }
          if (row.kind === 'issue') {
            const critical = isCritical(row.severity);
            const action = row.origin === 'compliance' && row.lifecycle === 'OPEN'
              ? <Button label="Mark actioned" variant="secondary" disabled={actioningId === row.id} onClick={() => void actionFinding(row.id)} />
              : issuePresentationAction(row)
                ? <Button label={issuePresentationAction(row)!} variant="secondary" disabled tooltip="Action will be enabled when its remediation workflow is available." />
                : undefined;
            const issueName = row.displayName ?? words(row.code);
            return (
              <div className="pay-run-compliance-issue-block" data-testid={`compliance-issue-${row.id}`}>
                <div
                  className={`pay-run-compliance-issue-card mgo-background-${critical ? 'critical' : 'caution'} mgo-border-${critical ? 'critical' : 'caution'}-soft`}
                  data-testid={`compliance-issue-card-${row.id}`}
                  role="note"
                >
                  <span className={`pay-run-compliance-issue-icon mgo-text-xs mgo-color-${critical ? 'critical' : 'caution'}`}>
                    <Icon name={critical ? 'status-critical' : 'status-caution'} />
                  </span>
                  <span className="pay-run-compliance-issue-copy mgo-text-s">
                    <strong>{critical ? 'Cannot start pay — ' : 'Warning. '}<span>{issueName}</span>.</strong>
                    <span>{row.message}</span>
                  </span>
                  {action}
                </div>
              </div>
            );
          }
          return (
            <span className="pay-run-compliance-employee">
              <TruncatedTableText text={row.displayName} className="mgo-weight-strong" />
              <TruncatedTableText text={row.employeeNumber} className="mgo-color-muted" />
            </span>
          );
        },
      },
      {
        field: 'roleTitle', header: 'Role', width: '8.8125rem', ...presentation,
        body: (node) => data(node).kind === 'employee'
          ? <TruncatedTableText text={(data(node) as ComplianceEmployeeRow).roleTitle || '—'} />
          : '',
      },
      { field: 'units', header: 'Units', width: '4.125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? quantity(Number((data(node) as ComplianceEmployeeRow).units)) : '' },
      { field: 'hourlyUnits', header: 'Hourly units', width: '6.125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? quantity(Number((data(node) as ComplianceEmployeeRow).hourlyUnits)) : '' },
      { field: 'gross', header: 'Gross', width: '4.875rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).gross)) : '' },
      { field: 'taxable', header: 'Taxable', width: '4.3125rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).taxable)) : '' },
      { field: 'tax', header: 'Tax', width: '4.0625rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).tax)) : '' },
      { field: 'stsl', header: 'STSL', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).stsl)) : '' },
      { field: 'btd', header: 'BTD', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? '—' : '' },
      { field: 'super', header: 'Super', width: '3.9375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).super)) : '' },
      { field: 'atd', header: 'ATD', width: '4.625rem', ...presentation, body: (node) => data(node).kind === 'employee' ? '—' : '' },
      { field: 'netPay', header: 'Net pay', width: '5.375rem', ...presentation, body: (node) => data(node).kind === 'employee' ? money(Number((data(node) as ComplianceEmployeeRow).netPay)) : '' },
      {
        field: 'status', header: 'Status', width: '9.6875rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'employee') return <Tag label={row.statusLabel} tone={row.issueTone} />;
          return '';
        },
      },
      {
        field: 'actions', header: ' ', width: '4.5rem', ...presentation,
        body: (node) => {
          const row = data(node);
          if (row.kind === 'employee' && !row.synthetic) {
            return (
              <DropdownMenu
                placement="bottom-end"
                trigger={<IconButton icon="more" label={`Actions for ${row.displayName}`} variant="tertiary" size="small" />}
                items={[
                  { label: 'View profile', icon: 'person', href: `/workers/worker-record/personal?worker=${encodeURIComponent(row.personId)}` },
                  { label: 'View timesheet', icon: 'history', disabled: true },
                  { label: 'Edit pay', icon: 'edit', disabled: true },
                  { label: 'Mark reviewed', icon: 'checkmark', disabled: true },
                  { separator: true },
                  { label: 'Send payslip', icon: 'document', disabled: true },
                  { label: 'View audit log', icon: 'history', href: `/payroll/pay-runs/${encodeURIComponent(payId)}?tab=audit` },
                  { separator: true },
                  { label: 'Generate termination', icon: 'delete', variant: 'destructive', disabled: true },
                ]}
              />
            );
          }
          return '';
        },
      },
    ];
  }, [actionFinding, actioningId, expandedKeys, payId]);

  const treeNodes = useMemo<TreeNode[]>(() => {
    const employeeNode = (employee: ComplianceEmployeeRow): TreeNode => ({
      key: `employee-${employee.personId}-${employee.employmentRecordId}`,
      data: employee,
      children: [
        ...employee.issues.map((issue) => ({
          key: `issue-${issue.id}`,
          leaf: true,
          data: { ...issue, kind: 'issue' },
        })),
        ...employee.bankingFindings.map((finding) => ({
          key: `banking-${finding.findingId}`,
          leaf: true,
          data: { ...finding, kind: 'banking' },
        })),
      ],
    });
    const groups: Array<{ key: string; label: 'Critical' | 'Warnings'; tone: 'critical' | 'caution'; rows: ComplianceEmployeeRow[] }> = [
      { key: 'group-critical', label: 'Critical', tone: 'critical', rows: visibleRows.filter((row) => row.issueTone === 'critical') },
      { key: 'group-warnings', label: 'Warnings', tone: 'caution', rows: visibleRows.filter((row) => row.issueTone !== 'critical') },
    ];
    return groups.filter((group) => group.rows.length > 0).map((group) => ({
      key: group.key,
      data: { kind: 'group', label: group.label, count: group.rows.length, issueTone: group.tone },
      children: group.rows.map(employeeNode),
    }));
  }, [visibleRows]);

  const totalPages = population?.pagination.totalPages ?? 0;
  const bankingAffectedCount = new Set(workerFindings(banking)
    .map((finding) => finding.personId || finding.employmentRecordId || finding.findingId)).size;
  const affectedEmployeeCount = Math.max(exceptions?.summary.affectedPeople ?? 0, bankingAffectedCount);
  const employeeCountLabel = `${affectedEmployeeCount} ${affectedEmployeeCount === 1 ? 'employee' : 'employees'}`;
  const initialLoading = loading && (!exceptions || !population);
  const expandableKeys = (nodes: TreeNode[]): string[] => nodes.flatMap((node) => node.children?.length
    ? [String(node.key), ...expandableKeys(node.children)] : []);
  const expansionKeys = expandableKeys(treeNodes);
  const allExpanded = expansionKeys.length > 0 && expansionKeys.every((key) => expandedKeys[key]);
  const expandAll = () => setExpandedKeys(Object.fromEntries(expansionKeys.map((key) => [key, true])));
  const collapseAll = () => setExpandedKeys({});
  const toggleAll = () => allExpanded ? collapseAll() : expandAll();
  const setFilterSelected = (filter: ComplianceFilter, checked: boolean) => {
    setSelectedFilters((current) => checked
      ? [...new Set([...current, filter])]
      : current.filter((value) => value !== filter));
    setPage(1);
  };

  return (
    <section className="mgo-stack mgo-gap-l" aria-label="Pay Run compliance" aria-busy={loading}>
      <PayRunBankingPanel
        businessRecordId={businessRecordId}
        payId={payId}
        details={banking}
        loading={bankingLoading}
        error={bankingError}
        notEvaluated={bankingNotEvaluated}
        onReload={onReloadBanking}
        onOverrideApplied={onFindingActioned}
        onNotice={onNotice}
        renderComplianceContent={({ onOverride }) => (
          <>
      <div className="mgo-split mgo-align-center mgo-gap-s pay-run-compliance-toolbar">
        <div
          className="mgo-cluster mgo-gap-s mgo-align-center pay-run-compliance-toolbar__controls"
          role="group"
          aria-label="Compliance table filters and actions"
        >
          <div className="pay-run-compliance-toolbar__search">
            <TextField
              label="Search employees or compliance warnings"
              hideLabel
              placeholder="Search employees or compliance warnings..."
              leadingIcon="search"
              clearable
              disabled={initialLoading}
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
              onClear={() => { setSearch(''); setPage(1); }}
            />
          </div>
          <Popover
            disabled={initialLoading}
            header="Filter employees"
            trigger={<Button variant="secondary" size="medium" disabled={initialLoading}>
              <span className="mgo-cluster mgo-gap-xs mgo-align-center">
                All employees
                <Icon name="indicator-down" />
              </span>
            </Button>}
          >
            <div className="mgo-stack mgo-gap-s">
              {complianceFilterOptions.map((option) => (
                <Checkbox
                  key={option.value}
                  id={`compliance-filter-${option.value}`}
                  label={option.label}
                  checked={selectedFilters.includes(option.value)}
                  onChange={(checked) => setFilterSelected(option.value as ComplianceFilter, checked)}
                />
              ))}
            </div>
          </Popover>
          <IconButton
            icon="assign-person"
            label="Expand all affected employees"
            variant="secondary"
            size="medium"
            disabled={initialLoading || treeNodes.length === 0}
            onClick={expandAll}
          />
          <DropdownMenu
            placement="bottom-end"
            trigger={<IconButton
              icon="more"
              label="More table actions"
              variant="secondary"
              size="medium"
              disabled={initialLoading || treeNodes.length === 0}
            />}
            items={[
              { label: 'Expand all', icon: 'expand-all', onClick: expandAll },
              { label: 'Collapse all', icon: 'collapse-all', onClick: collapseAll },
            ]}
          />
          {selectedFilters.length > 0 && (
            <Button label="Clear filters" variant="tertiary" size="small" onClick={() => setSelectedFilters([])} />
          )}
        </div>
        {exceptions && <span className="mgo-text-s mgo-color-muted">{employeeCountLabel}</span>}
      </div>

      {exceptions && exceptions.evaluationStatus !== 'completed' && (
        <Callout tone="caution" title="Compliance readiness not confirmed">
          {exceptions.readinessReason ? `Readiness is blocked: ${words(exceptions.readinessReason)}.` : 'Run the readiness evaluation after completing the required inputs and calculation.'}
        </Callout>
      )}
      {populationStatus === 'loading' && <Callout tone="info" title="Refreshing compliance">Results update when the employee snapshot is ready.</Callout>}
      {error && <Callout tone="critical" title="Compliance unavailable" action={<Button label="Retry" variant="secondary" size="small" onClick={() => void load()} />}>{error}</Callout>}

      {initialLoading && !error ? (
        <div
          className="mgo-cluster mgo-justify-center mgo-border-neutral"
          style={{ minHeight: '15rem' }}
          aria-busy="true"
          aria-label="Loading compliance results"
        >
          <span style={{ fontSize: 'var(--mgo-size-80)' }}><Spinner testId="pay-run-compliance-loading-spinner" /></span>
        </div>
      ) : !error && rows.length === 0 ? (
        <EmptyState variant={exceptions?.evaluationStatus === 'completed' ? 'success' : 'no-data'} title="No current compliance issues" description={exceptions?.evaluationStatus === 'completed' ? 'The latest canonical evaluation has no findings or outstanding input review issues.' : 'No findings are persisted, but readiness has not been confirmed.'} />
      ) : !error && treeNodes.length === 0 ? (
        <EmptyState variant="no-data" title="No employees match your search" description="Change or clear the current search." actions={[<Button key="clear" label="Clear search" variant="secondary" onClick={() => setSearch('')} />]} />
      ) : !error ? (
        <div className="pay-run-compliance-table">
          <TreeTable
            value={treeNodes}
            columns={columnsFor(onOverride, (
              <span className="mgo-cluster mgo-gap-xs mgo-align-center">
                <IconButton
                  icon="sort"
                  label={allExpanded ? 'Collapse all compliance rows' : 'Expand all compliance rows'}
                  variant="tertiary"
                  size="small"
                  onClick={toggleAll}
                  disabled={expansionKeys.length === 0}
                  testId="pay-run-compliance-expand-all"
                />
                <span>Employee</span>
              </span>
            ))}
            expandedKeys={expandedKeys}
            onToggle={setExpandedKeys}
            testId="pay-run-exceptions-table"
            ptProps={{
              rowClassName: (node) => ({
                'pay-run-compliance-group-row--critical': node.data.kind === 'group' && node.data.issueTone === 'critical',
                'pay-run-compliance-group-row--caution': node.data.kind === 'group' && node.data.issueTone === 'caution',
                'pay-run-compliance-employee-row--critical': node.data.kind === 'employee' && node.data.issueTone === 'critical',
                'pay-run-compliance-employee-row--caution': node.data.kind === 'employee' && node.data.issueTone === 'caution',
                'pay-run-compliance-issue-row--critical': (node.data.kind === 'issue' && isCritical(node.data.severity))
                  || (node.data.kind === 'banking' && bankingIsCritical(node.data)),
                'pay-run-compliance-issue-row--caution': (node.data.kind === 'issue' && !isCritical(node.data.severity))
                  || (node.data.kind === 'banking' && !bankingIsCritical(node.data)),
              }),
              tableStyle: { minWidth: 'max(100%, 83.4375rem)', width: '100%' },
            }}
            emptyMessage="No compliance issues."
          />
        </div>
      ) : null}

      {!initialLoading && !error && population && totalPages > 1 && <Paginator first={(page - 1) * pageSize}
        itemsPerPage={pageSize} totalRecords={population.pagination.totalItems}
        onPageChange={(event) => setPage(event.page + 1)} />}
          </>
        )}
      />
    </section>
  );
};
